"use client";

import { MoreHorizontal, DollarSign } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { BidButton } from "@/components/bid/BidButton";
import { BidHistoryModal } from "@/components/bid/BidHistoryModal";
import { ListingLink } from "@/components/ListingLink";
import { generateListingUrl } from "@/components/ListingLink";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { type ListingDetailedType } from "@/hooks/use-listings";
import { useSession } from "@/lib/auth/client";
import { useSavedListings } from "@/lib/dao/SavedListingDao";
import { useUpdateListing } from "@/lib/hooks/generated/tanstack-query";
import { handleListingStateTransition } from "@/lib/listing/listing";
import {
  ListingStateMachine,
  type ListingAction,
} from "@/lib/listing/listing-state-machine";

interface ListingMenuActionsProps {
  listing: ListingDetailedType;
  userRole: "tenant" | "landlord" | "admin";
}

export function ListingMenuActions({
  listing,
  userRole,
}: ListingMenuActionsProps) {
  const { data: session } = useSession();
  // Use useMemo to prevent creating new instance on every render
  const updateListing = useUpdateListing();
  const [open, setOpen] = useState(false);
  const {
    isListingSaved,
    toggleSavedListing,
    isLoading: savedListingsLoading,
  } = useSavedListings();
  const isSaved = isListingSaved(listing.id);

  // Find the user's comment or reply for this listing
  const userComment = listing.comments.find(
    (comment) => comment.userId === session?.user?.id,
  );

  const userReply = listing.comments
    .flatMap((comment) => comment.replies)
    .find((reply) => reply?.userId === session?.user?.id);

  const renderTenantActions = () => (
    <>
      {listing.atAuction && (
        <DropdownMenuItem asChild>
          <BidButton
            auction={listing.atAuction}
            listing={listing}
            bids={listing.atAuction.bids}
            renderAs="dropdown-item"
          />
        </DropdownMenuItem>
      )}
      <DropdownMenuItem asChild>
        <ListingLink listing={listing} className="w-full">
          <span className="font-geist">View listing</span>
        </ListingLink>
      </DropdownMenuItem>
      {userComment || userReply ? (
        <DropdownMenuItem asChild>
          <ListingLink
            listing={listing}
            deepLinkTo="comments"
            className="w-full"
          >
            <span className="font-geist">View conversation</span>
          </ListingLink>
        </DropdownMenuItem>
      ) : null}
      <DropdownMenuItem
        onClick={async (e) => {
          e.preventDefault();
          try {
            await toggleSavedListing(listing.id);
            setOpen(false);
            toast.success(
              isSaved ? "Removed from favorites" : "Added to favorites",
            );
          } catch (error) {
            console.error("Failed to toggle favorite:", error);
            toast.error("Failed to update favorite");
          }
        }}
      >
        <span className="font-geist">
          {isSaved ? "Remove from favorites" : "Add to favorites"}
        </span>
      </DropdownMenuItem>
    </>
  );

  const stateMachine = new ListingStateMachine();
  const landlordAvailableActions = stateMachine.getAvailableActionsWithInfo(
    listing.status,
    "landlord",
  );
  const landlordCanEdit = stateMachine.canLandlordEdit(listing);
  const adminAvailableActions = stateMachine.getAvailableActionsWithInfo(
    listing.status,
    "admin",
  );
  const adminCanEdit = stateMachine.canAdminEdit(listing);

  const handleStateTransition = async (action: ListingAction) => {
    if (!session?.user?.id) return;

    // Use shared utility for state transition with edit callback
    const handleEdit = () => {
      // Navigate to listing editor
      window.location.href = generateListingUrl(listing, { route: "landlord" });
    };

    await handleListingStateTransition(
      action,
      listing,
      updateListing,
      handleEdit,
    );
  };

  // Add loading guard to prevent infinite loops during data loading
  if (savedListingsLoading) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        disabled
        aria-label="Loading actions..."
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </Button>
    );
  }

  const renderLandlordActions = () => {
    return (
      <>
        {listing.atAuction && (
          <>
            <DropdownMenuItem asChild>
              <BidHistoryModal
                auction={listing.atAuction}
                bids={listing.atAuction.bids}
                trigger={
                  <div
                    className={`font-geist w-full rounded-sm px-2 py-1.5 text-sm ${
                      listing.atAuction.bids.length > 0
                        ? "cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        : "cursor-default text-muted-foreground"
                    }`}
                  >
                    View bids & bidders ({listing.atAuction.bids.length})
                  </div>
                }
              />
            </DropdownMenuItem>

            <DropdownMenuSeparator />
          </>
        )}

        {landlordCanEdit && (
          <DropdownMenuItem asChild>
            <ListingLink listing={listing} route="landlord" className="w-full">
              <span className="font-geist">Edit listing</span>
            </ListingLink>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem asChild>
          <ListingLink listing={listing} className="w-full">
            <span className="font-geist">
              {stateMachine.canPreview(listing)
                ? "Preview listing"
                : "View listing"}
            </span>
          </ListingLink>
        </DropdownMenuItem>

        {landlordAvailableActions.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {landlordAvailableActions.map((action) => (
              <DropdownMenuItem
                key={action.type}
                onClick={() => handleStateTransition(action.type)}
              >
                <span className="font-geist">{action.getLabel()}</span>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </>
    );
  };

  const renderAdminActions = () => {
    return (
      <>
        {listing.atAuction && (
          <>
            <DropdownMenuItem asChild>
              <BidHistoryModal
                auction={listing.atAuction}
                bids={listing.atAuction.bids}
                trigger={
                  <div
                    className={`w-full rounded-sm px-2 py-1.5 text-sm ${
                      listing.atAuction.bids.length > 0
                        ? "cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        : "cursor-default text-muted-foreground"
                    }`}
                  >
                    <DollarSign className="mr-2 h-4 w-4" aria-hidden="true" />
                    View bids & bidders ({listing.atAuction.bids.length})
                  </div>
                }
              />
            </DropdownMenuItem>

            <DropdownMenuSeparator />
          </>
        )}

        {adminCanEdit && (
          <DropdownMenuItem asChild>
            <a
              href={generateListingUrl(listing, { route: "landlord" })}
              className="w-full"
            >
              <span className="font-geist">Edit listing</span>
            </a>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem asChild>
          <ListingLink listing={listing} className="w-full">
            <span className="font-geist">View listing</span>
          </ListingLink>
        </DropdownMenuItem>

        {adminAvailableActions.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {adminAvailableActions.map((action) => (
              <DropdownMenuItem
                key={action.type}
                onClick={() => handleStateTransition(action.type)}
                className="text-red-600 focus:text-red-600"
              >
                {(() => {
                  const IconComponent = action.getIcon();
                  return <IconComponent className="h-4 w-4" />;
                })()}
                <span className="font-geist">{action.getLabel()}</span>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </>
    );
  };

  const renderActions = () => {
    switch (userRole) {
      case "tenant":
        return renderTenantActions();
      case "landlord":
        return renderLandlordActions();
      case "admin":
        return renderAdminActions();
      default:
        return null;
    }
  };

  return (
    <DropdownMenu
      key={listing.id}
      modal={false}
      open={open}
      onOpenChange={setOpen}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="link"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label={`Actions for ${listing.title}`}
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={4}
        className="z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
      >
        {renderActions()}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
